import { Plus, Save, Trash2 } from "lucide-react";
import {
  useCreate,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

import { PERMISSIONS } from "../providers/commons/canAccess";
import type { Role } from "../types";

/**
 * Permission matrix of the "Roles and access" settings (docs/design/Settings).
 * Saves the roles table, separately from the configuration form around it.
 * The admin role is the technical operator and stays out of the matrix.
 */
export const SettingsRoles = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [create] = useCreate<Role>();
  const { data: roles, refetch } = useGetList<Role>("roles", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
  });
  // Unsaved permission edits, by role id
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const visible =
    roles?.filter((role) => role.key !== "admin" && role.key !== "developer") ??
    [];
  const permissionsOf = (role: Role) => draft[role.id] ?? role.permissions;

  const toggle = (role: Role, permission: string, checked: boolean) => {
    const current = permissionsOf(role);
    setDraft({
      ...draft,
      [role.id]: checked
        ? [...current, permission]
        : current.filter((p) => p !== permission),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const role of visible) {
        if (!draft[role.id]) continue;
        await dataProvider.update<Role>("roles", {
          id: role.id,
          data: { permissions: draft[role.id] },
          previousData: role,
        });
      }
      setDraft({});
      await refetch();
      notify("crm.settings.saved");
    } catch {
      notify("crm.settings.save_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const addRole = () => {
    const name = newName.trim();
    if (!name) return;
    create(
      "roles",
      { data: { name, permissions: ["leads.own"] } },
      {
        onSuccess: () => {
          setNewName("");
          refetch();
        },
        onError: () => notify("crm.settings.save_error", { type: "error" }),
      },
    );
  };

  const deleteRole = async (role: Role) => {
    try {
      await dataProvider.delete<Role>("roles", {
        id: role.id,
        previousData: role,
      });
      await refetch();
    } catch {
      // The sales.role_id foreign key refuses deleting a role in use
      notify("crm.settings.roles.in_use", { type: "error" });
    }
  };

  return (
    <Card id="roles">
      <CardContent className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("crm.settings.roles.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.roles.description")}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-2 text-left font-medium">
                  {translate("crm.settings.roles.action")}
                </th>
                {visible.map((role) => (
                  <th key={role.id} className="px-2 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      {role.name}
                      {role.key == null ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          aria-label={translate("crm.settings.roles.delete")}
                          onClick={() => deleteRole(role)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSIONS.map((permission) => (
                <tr key={permission} className="border-b last:border-0">
                  <td className="py-2 pr-2">
                    {translate(`crm.settings.roles.permissions.${permission}`)}
                  </td>
                  {visible.map((role) => (
                    <td key={role.id} className="px-2 py-2 text-center">
                      <Checkbox
                        aria-label={`${role.name}: ${translate(
                          `crm.settings.roles.permissions.${permission}`,
                        )}`}
                        checked={permissionsOf(role).includes(permission)}
                        disabled={role.locked}
                        onCheckedChange={(checked) =>
                          toggle(role, permission, checked === true)
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder={translate("crm.settings.roles.name")}
            className="w-48"
          />
          <Button
            type="button"
            variant="outline"
            onClick={addRole}
            disabled={!newName.trim()}
          >
            <Plus />
            {translate("crm.settings.roles.new")}
          </Button>
          <Button
            type="button"
            className="ml-auto"
            onClick={save}
            disabled={saving || Object.keys(draft).length === 0}
          >
            <Save />
            {saving
              ? translate("crm.settings.saving")
              : translate("ra.action.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
